"use client";

import { useLayoutEffect } from "react";
import { DEVICE_SAFE_INSET_PROBE_STYLE } from "@/lib/virtualspace/portal-visual-viewport";
import { stampDeviceSafeTop } from "@/lib/virtualspace/use-portal-visual-viewport";
import {
  STANDALONE_DISPLAY_MODE_QUERIES,
  syncStandaloneDisplayMode,
} from "@/lib/virtualspace/standalone-display-mode";

/**
 * Marks `<html data-vs-standalone="1">` when the app is an installed PWA
 * and stamps the shared Dynamic Island inset on every surface — not only
 * Spatialist. CSS media queries often never match on iOS, and env() can
 * be 0 / 20px while chrome still paints under the island. Must agree with
 * the root-layout boot script (AD #594): an iOS 26 short viewport is a
 * lie, not “OS already reserved the bar.”
 */
export default function StandaloneSafeArea() {
  useLayoutEffect(() => {
    const root = document.documentElement;
    const probe = document.createElement("div");
    probe.setAttribute("data-vs-device-safe-probe", "");
    probe.setAttribute("aria-hidden", "true");
    probe.style.cssText = DEVICE_SAFE_INSET_PROBE_STYLE;
    document.body.appendChild(probe);

    const sync = () => {
      syncStandaloneDisplayMode(root);
      stampDeviceSafeTop(root, { probe });
    };

    sync();

    const media = STANDALONE_DISPLAY_MODE_QUERIES.map((query) =>
      window.matchMedia(query),
    );
    for (const query of media) query.addEventListener("change", sync);
    const vv = window.visualViewport;
    vv?.addEventListener("resize", sync);
    window.addEventListener("resize", sync);
    window.addEventListener("orientationchange", sync);
    window.addEventListener("pageshow", sync);

    return () => {
      for (const query of media) query.removeEventListener("change", sync);
      vv?.removeEventListener("resize", sync);
      window.removeEventListener("resize", sync);
      window.removeEventListener("orientationchange", sync);
      window.removeEventListener("pageshow", sync);
      probe.remove();
      root.removeAttribute("data-vs-standalone");
    };
  }, []);

  return null;
}
